import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FilePenLine, X, Check, Eye, EyeOff, MessageSquarePlus, Send } from "lucide-react";

const STATUS_LABEL = { new: "New", reviewed: "Reviewed", completed: "Completed" };
const STAGE_LABEL = { received: "Received", in_progress: "In progress", filed: "Filed", refund_sent: "Refund sent", info: "Info" };
const REASON_LABEL = {
  missing_document: "Missing document",
  corrected_document: "Corrected doc received",
  dependent_change: "Dependent change",
  filing_status_change: "Filing status change",
  income_change: "Income correction",
  credit_missed: "Missed credit/deduction",
  other: "Other",
};
const CATEGORY_LABEL = {
  original_return: "Original return",
  corrected_document: "Corrected doc",
  supporting_docs: "Supporting docs",
};

export default function AdminAmendments() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const load = () => api.get("/admin/amendment").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id, reveal = false) => {
    try {
      const r = await api.get(`/admin/amendment/${id}${reveal ? "?reveal=true" : ""}`);
      setDetail(r.data); setRevealed(reveal);
    } catch { toast.error("Could not load"); }
  };
  const closeDetail = () => { setDetail(null); setRevealed(false); };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/amendment/${id}?status=${status}`);
      toast.success("Updated"); load();
      if (detail?.id === id) open(id, false); // re-mask after any write
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const postUpdate = async (id, stage, message) => {
    if (!message.trim()) { toast.error("Add a message first"); return; }
    try {
      await api.post(`/admin/amendment/${id}/status-update`, { stage, message });
      toast.success("Update posted — visible in client portal");
      open(id, false); // re-mask after any write
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><FilePenLine size={14} /> Amendments · 1040-X</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Amendment requests.</h1>

        {rows.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No amendment requests yet. Share your link: <code className="text-xs">/amend</code></Card>
        ) : (
          <div className="space-y-2" data-testid="amend-list">
            {rows.map(r => (
              <Card key={r.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`amend-row-${r.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-serif text-lg">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-muted-foreground">{[r.email, r.phone].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="text-xs text-muted-foreground hidden md:block">
                  TY{r.tax_year} · {REASON_LABEL[r.reason] || r.reason} · {r.uploads_count} files
                </div>
                {r.referrer_ref && (
                  <div className="text-[10px] px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20" data-testid={`amend-referrer-${r.id}`}>
                    via {r.referrer_ref}
                  </div>
                )}
                <span className={`text-[10px] label-caps px-2 py-1 rounded ${r.status === "completed" ? "bg-primary/15 text-primary" : r.status === "reviewed" ? "bg-accent/15 text-accent" : "bg-secondary text-foreground"}`}>
                  {STATUS_LABEL[r.status] || r.status}
                </span>
                <div className="text-[10px] text-muted-foreground shrink-0">{new Date(r.submitted_at).toLocaleDateString()}</div>
                <Button size="sm" variant="outline" onClick={() => open(r.id)} data-testid={`amend-open-${r.id}`}><Eye size={12} className="mr-1" /> View</Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={closeDetail}>
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="amend-detail">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <div className="label-caps text-[10px]">Amendment · TY{detail.tax_year} · {detail.status}</div>
                <div className="font-serif text-2xl">{detail.first_name} {detail.last_name}</div>
                <div className="text-xs text-muted-foreground">{[detail.email, detail.phone].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant={revealed ? "default" : "outline"} onClick={() => open(detail.id, !revealed)} data-testid="amend-reveal-btn">
                  {revealed ? <><EyeOff size={12} className="mr-1" /> Hide SSN</> : <><Eye size={12} className="mr-1" /> Reveal SSN</>}
                </Button>
                {detail.status !== "reviewed" && detail.status !== "completed" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(detail.id, "reviewed")} data-testid="amend-mark-reviewed">Mark reviewed</Button>
                )}
                {detail.status !== "completed" && (
                  <Button size="sm" onClick={() => setStatus(detail.id, "completed")} data-testid="amend-mark-completed"><Check size={12} className="mr-1" /> Complete</Button>
                )}
                <button onClick={closeDetail} className="p-2 rounded-full hover:bg-secondary" aria-label="Close"><X size={16} /></button>
              </div>
            </div>

            <div className="p-6 space-y-6 text-sm">
              <div>
                <div className="label-caps mb-1">Reason</div>
                <div>{REASON_LABEL[detail.reason] || detail.reason}</div>
              </div>
              {detail.description && (
                <div>
                  <div className="label-caps mb-1">Details</div>
                  <div className="text-xs whitespace-pre-wrap">{detail.description}</div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><div className="label-caps text-[9px]">SSN on return</div><div className="font-mono text-sm">{detail.ssn || "—"}</div></div>
                <div><div className="label-caps text-[9px]">Fee</div><div className="text-sm">${detail.fee || 75}</div></div>
              </div>

              <div>
                <div className="label-caps mb-2">Documents</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(detail.uploads || {}).map(([k, files]) => (
                    <div key={k} className="p-2 border border-border rounded text-xs">
                      <div className="font-medium mb-1">{CATEGORY_LABEL[k] || k} ({files?.length || 0})</div>
                      {(files || []).length === 0 ? <div className="text-muted-foreground text-[10px]">—</div> : (
                        <div className="space-y-1">
                          {files.map((p, idx) => (
                            <a key={idx} href={`${BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer" className="block underline truncate text-primary" data-testid={`amend-doc-${k}-${idx}`}>
                              Open file {idx + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {detail.notes && (
                <div>
                  <div className="label-caps mb-1">Notes</div>
                  <div className="text-xs whitespace-pre-wrap italic">{detail.notes}</div>
                </div>
              )}

              <StatusUpdatesPanel amend={detail} onPost={postUpdate} />

              <div className="border-t border-border pt-4">
                <div className="label-caps mb-2">Signature</div>
                <div className="text-xs">Signed by <strong>{detail.signature}</strong> on {detail.signed_date}</div>
                <div className="text-[10px] mt-2 text-muted-foreground">Fee acknowledged: {detail.fee_ack ? "✓" : "✗"}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const STAGES = [
  { key: "received", label: "Received" },
  { key: "in_progress", label: "In progress" },
  { key: "filed", label: "Filed" },
  { key: "refund_sent", label: "Refund sent" },
  { key: "info", label: "Info" },
];

function StatusUpdatesPanel({ amend, onPost }) {
  const [stage, setStage] = React.useState("info");
  const [message, setMessage] = React.useState("");
  const updates = (amend.status_updates || []).slice().reverse();
  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-2 mb-3 text-primary">
        <MessageSquarePlus size={12} />
        <span className="label-caps text-[10px]">Updates for client portal</span>
      </div>
      {updates.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="amend-updates-list">
          {updates.map((u) => (
            <div key={u.id} className="border-l-2 border-primary pl-3 text-xs">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="label-caps text-[9px]">{STAGE_LABEL[u.stage] || u.stage}</span>
                <span className="text-[9px] text-muted-foreground">{new Date(u.posted_at).toLocaleString()}</span>
              </div>
              <div className="whitespace-pre-wrap">{u.message}</div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 items-end">
        <div className="col-span-1">
          <div className="label-caps text-[9px] mb-1">Stage</div>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs" data-testid="amend-update-stage">
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-span-3">
          <div className="label-caps text-[9px] mb-1">Message to client</div>
          <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. 1040-X mailed to the IRS today. Expect 8-12 weeks for processing."
            className="w-full rounded-md border border-input bg-background p-2 text-xs" data-testid="amend-update-message" />
        </div>
      </div>
      <Button size="sm" className="mt-2" onClick={async () => { await onPost(amend.id, stage, message); setMessage(""); }} data-testid="amend-update-post">
        <Send size={12} className="mr-1" /> Post update
      </Button>
    </div>
  );
}
